// components/steps/StepSummary.jsx
import React from 'react';
import { CheckCircle } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';

// Returns the ordinal suffix for a day (1st, 2nd, 3rd, 4th…)
const getOrdinal = (n) => {
  const s = ['th','st','nd','rd'], v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// Formats ISO date as "Month Dth YYYY"
const formatDate = (isoString) => {
  if (!isoString) return 'Not specified';
  const d = new Date(isoString);
  const month = d.toLocaleString('default', { month: 'long' });
  const day   = d.getDate();
  const year  = d.getFullYear();
  return `${month} ${day}${getOrdinal(day)} ${year}`;
};

// Formats budget whether string or object { amount, currency }
const formatBudget = (b) => {
  if (b == null || b === '') return 'Not specified';
  if (typeof b === 'string') return b;
  if (typeof b === 'object' && b.amount != null && b.currency) {
    const num = Number(String(b.amount).replace(/,/g, ''));
    if (!isNaN(num)) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: b.currency
      }).format(num);
    }
  }
  return String(b);
};

// Renders companions from either string or {type, hasKids?, count?}
const renderCompanions = (c) => {
  if (!c) return 'Not specified';
  if (typeof c === 'string') return c;
  const { type, hasKids, count } = c;
  switch (type) {
    case 'Solo':               return 'Solo';
    case 'Other':              return 'Other';
    case 'Partner':
      return hasKids
        ? `Partner with ${count} child${count > 1 ? 'ren' : ''}`
        : 'Partner (no children)';
    case 'Friend Group':       return `${count} friend${count > 1 ? 's' : ''}`;
    case 'Business Associate': return `${count} colleague${count > 1 ? 's' : ''}`;
    default:                   return 'Not specified';
  }
};

const StepSummary = ({ data = {}, onSubmit, onEdit = () => {} }) => {
  const { isSignedIn } = useUser();
  const { openSignIn } = useClerk();

  const handleClick = () => {
    if (!isSignedIn) openSignIn();
    else onSubmit(data);
  };

  const entries = [
    { label: 'From',                value: data.from || 'Not specified' },
    { label: 'Destination',         value: data.destination || 'Not specified' },
    { label: 'Travel Dates',
      value: data.dates?.start && data.dates?.end
        ? `${formatDate(data.dates.start)} → ${formatDate(data.dates.end)}`
        : 'Not specified'
    },
    { label: 'Budget',              value: formatBudget(data.budget) },
    { label: 'Hotel Reservation',   value: data.hasHotelReservation ? 'Yes' : 'No' },
    { label: 'Interests',           value: data.interests?.length
        ? data.interests.join(', ')
        : 'None'
    },
    { label: 'Transport',           value: data.transport || 'Not specified' },
    { label: 'Companions',          value: renderCompanions(data.companions) },
    { label: 'Dietary Preferences', value: data.diet?.length
        ? data.diet.join(', ')
        : 'None'
    },
    { label: 'Trip Mood',           value: data.mood?.length
        ? data.mood.join(', ')
        : 'None'
    },
    { label: 'Experience Level',    value: data.experience || 'Not specified' },
    { label: 'Additional Details',  value: data.additionalDescription || 'None' },
  ];

  return (
    <div className="bg-white/60 backdrop-blur-lg p-8 rounded-2xl shadow-xl max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
        Review Your Trip Info
      </h2>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        {entries.map(({ label, value }) => (
          <div key={label} className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
            <div>
              <dt className="font-medium text-gray-700">{label}</dt>
              <dd className="mt-1 text-gray-900">{value}</dd>
            </div>
          </div>
        ))}
      </dl>
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={onEdit}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
        >
          Edit
        </button>
        <button
          onClick={handleClick}
          className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition"
        >
          Submit &amp; Plan My Trip
        </button>
      </div>
    </div>
  );
};

export default StepSummary;
